import { useEffect, useMemo } from 'react';

import { EMAIL_TEMPLATE_TYPE_OPTIONS } from 'constants/email-config';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmailTemplatePreview } from 'components/EmailTemplatePreview/EmailTemplatePreview';
import { FormMessage, MessageType } from 'components/FormComponents';
import {
  Button,
  Input,
  InputGroup,
  ParamTextarea,
  RichTextEditor,
  Select,
} from 'components/FormComponents';

export const EMAIL_TEMPLATE_CATEGORIES = [
  { value: 'candidate', label: 'Candidate' },
  { value: 'referee', label: 'Referee' },
];

export const EmailTemplateForm = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading,
  showSupportFooterInPreview = true,
}) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      category: 'candidate',
      type: '',
      subject: '',
      body: '',
      ...initialValues,
    },
  });

  const category = watch('category');

  const textParams = useMemo(() => {
    const candidateParams = {
      candidate: '{{candidate}}',
      candidateFirstName: '{{candidateFirstName}}',
      candidateFullName: '{{candidateFullName}}',
      candidateLastName: '{{candidateLastName}}',
      job: '{{job}}',
      company: '{{company}}',
    };
    const refereeParams = {
      referee: '{{referee}}',
      refereeFirstName: '{{refereeFirstName}}',
      refereeFullName: '{{refereeFullName}}',
      refereeLastName: '{{refereeLastName}}',
    };
    return {
      ...candidateParams,
      ...(category === 'referee' ? refereeParams : {}),
    };
  }, [category]);

  useEffect(() => {
    if (initialValues) {
      Object.entries(initialValues).forEach(([key, value]) => setValue(key, value));
    }
  }, [initialValues, setValue]);

  return (
    <div className="flex w-full flex-col gap-8 lg:flex-row lg:flex-nowrap lg:items-stretch">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full shrink-0 lg:max-w-5xl lg:basis-1/2">
        <InputGroup title={t('emailTemplates.form.name')} error={errors.name?.message}>
          <Input
            name="name"
            placeholder={t('emailTemplates.form.namePlaceholder')}
            inputProps={register('name', { required: t('emailTemplates.form.nameRequired') })}
            error={errors.name?.message}
          />
        </InputGroup>
        {/* Only UX is changed to audience for better UX. DB and backend are still using category. */}
        <InputGroup
          title={t('emailTemplates.form.audience')}
          description={t('emailTemplates.form.audienceDescription')}>
          <Controller
            name="category"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Select name="category" value={value} onChange={e => onChange(e.target.value)}>
                {EMAIL_TEMPLATE_CATEGORIES.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select>
            )}
          />
        </InputGroup>
        {/* Only UX is changed to Event for better UX. DB and backend are still using type. */}
        <InputGroup
          title={t('emailTemplates.form.event')}
          description={t('emailTemplates.form.eventDescription')}
          error={errors.type?.message}>
          <Controller
            name="type"
            control={control}
            rules={{ required: t('emailTemplates.form.typeRequired') }}
            render={({ field: { value, onChange } }) => (
              <Select
                name="type"
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                error={errors.type?.message}>
                <Select.Item value="" disabled>
                  {t('emailTemplates.form.selectType')}
                </Select.Item>
                {EMAIL_TEMPLATE_TYPE_OPTIONS.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {t(`emailTemplates.type.${option.value}`, { defaultValue: option.label })}
                  </Select.Item>
                ))}
              </Select>
            )}
          />
        </InputGroup>
        <Controller
          control={control}
          name="subject"
          rules={{ required: 'This field is required.' }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <InputGroup title={t('emailTemplates.form.subject')} error={errors.subject?.message}>
              <ParamTextarea
                value={value || ''}
                onChange={onChange}
                textParams={textParams}
                placeholder="Enter subject..."
                error={errors.subject?.message}
              />
            </InputGroup>
          )}
        />
        <InputGroup title={t('emailTemplates.form.body')} error={errors.body?.message}>
          <Controller
            name="body"
            control={control}
            rules={{ required: t('emailTemplates.form.bodyRequired') }}
            render={({ field: { value, onChange } }) => (
              <RichTextEditor
                value={value}
                onChange={onChange}
                error={errors.body?.message}
                textParams={textParams}
                storeTextParamsAsMustache
              />
            )}
          />
        </InputGroup>
        {!showSupportFooterInPreview && (
          <FormMessage
            type={MessageType.Info}
            message="Support footer will not be included in the email. It is turned off in the brand settings"
            noMargin
            closeable={false}
          />
        )}
        <div className="mt-4 flex gap-2">
          <Button
            type="primary"
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
            className="u-width-100">
            {t('emailTemplates.form.save')}
          </Button>
          {onCancel && (
            <Button type="secondary" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
        </div>
      </form>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <EmailTemplatePreview
          className="min-h-[18rem] w-full flex-1 lg:min-h-0"
          subject={watch('subject')}
          body={watch('body')}
          showSupportFooter={showSupportFooterInPreview}
          showActions
          textParams={textParams}
        />
      </div>
    </div>
  );
};
